/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getCobertura,
  getEsteira,
  getResumo,
  listarDepartamentos,
  IBotDaEmpresa,
  IPeriodoParams,
  IResponsaveisPorEmpresa,
  IVinculoBot,
  SituacaoEmpresa,
  StatusEsteira,
  STALE_TEMPO_REAL,
} from '../api';
import { C, FONTE_MONO, FONTE_TEXTO, TONES, TONE_DO_STATUS, Tone } from '../paleta';
import { ST_META } from '../utils/ordemStatus';
import { nomeEmpresa } from '../utils/derivarProcessos';
import {
  aplicarRegimeProprioNoResumo,
  ColunaResumo,
  filtrarEmpresasResumo,
  filtrarPorSituacaoCadastral,
  FiltrosResumo,
  FILTROS_RESUMO_VAZIOS,
  IEmpresaResumoRecortada,
  opcoesComValorSelecionado,
  opcoesDeAreas,
  opcoesDeResponsaveis,
  ordenarEmpresasResumo,
  OrdenacaoResumo,
  rotuloForaDasRegras,
  SITUACOES_CADASTRAIS,
  SITUACOES_KPI,
  SituacaoCadastral,
  trilhosDaEmpresa,
} from '../utils/derivarResumo';
import {
  aoAlternarStatus,
  aoEscolherRegime,
  FiltrosCompartilhados,
  regimesDisponiveis,
} from '../utils/filtrosCompartilhados';
import { SelectFiltro } from '../components/SelectFiltro';
import { FiltroStatus } from '../components/FiltroStatus';
import { NomeEmpresa } from '../components/NomeEmpresa';
import { aoTrocarModoBusca, ModoBusca } from '../utils/codigoEmpresa';
import { regimeProprio } from '../utils/regimeEfetivo';
import { resumirLista } from '../utils/resumirLista';
import { areasDaLinha, paresAreaResponsavel, tooltipDosPares } from '../utils/paresAreaResponsavel';
import { useApelidos } from '../useApelidos';
import { textoPct } from '../utils/painelDoNo';
import { ordemDoFluxo } from '../utils/ordemDoFluxo';
import {
  areaDeCadaBot,
  indiceDeBotsEsperados,
  PercursoDaEmpresa,
  PercursoPorModulo,
  percursoPorModulo,
} from '../utils/percursoDaEmpresa';
import { AlvoModalBot, ModalBotDaEmpresa, motivoDoBot } from '../components/ModalBotDaEmpresa';
import {
  CARTAO,
  estiloPilula,
  EYEBROW,
  Faixa,
  fundoDaAba,
  Icone,
  TituloSecao,
} from '../components/Vidro';

/**
 * Aba RESUMO — porta o template `isResumo` do protótipo (`sistema/index.html` linhas
 * 381-534) 1:1: KPIs de situação clicáveis, card "Empresas" com filtros/busca/limpar,
 * banner de período com "Ver todas as empresas" (que desde 09/09 ignora SÓ o recorte
 * de PERÍODO — não mexe mais em empresa inativa), tabela em grid de 7 colunas com
 * ordenação (incluindo Grupo — no-op no protótipo, corrigido, C2 §7 item 3), pílulas
 * de bots com setas de dependência, e paginação client-side de 20 por página. Sem
 * nenhuma linha no recorte, a tabela mostra estado vazio honesto (revisão 09/09) —
 * o protótipo só renderizava o cabeçalho.
 *
 * Reforma dos filtros (pedido 08/09): as pílulas de status descem para linha própria
 * abaixo dos filtros gerais; o select "Todas as empresas" saiu (só desta aba); a
 * busca ganhou seletor de TIPO ("Razão social" | "ID") e casa só no campo escolhido;
 * trocar o tipo LIMPA o termo digitado (mantê-lo zerava o resultado em silêncio).
 * Ajuste visual (pedido 08/09): seletor de tipo + textbox unificados num controle
 * único com fundo cinza-claro da paleta — ver comentário no JSX da busca.
 * Inativas/fora da vigência vêm do BFF com flags e são excluídas GLOBALMENTE por
 * default (na fonte, index.tsx). O filtro de SITUAÇÃO CADASTRAL (pedido 09/09,
 * "Empresas ativas" | "Empresas inativas" | "Ativas e inativas") é a ÚNICA forma de
 * revelá-las, e só nesta aba: ela recebe de index.tsx um segundo dataset
 * (`empresasComInativas`) com as flags preservadas. Quando aparecem, a linha traz o
 * rótulo "inativa" (ativa=false) ou "fora de vigência" abaixo do código. O "Ver
 * todas as empresas" do banner deixou de revelar inativas (era um segundo controle
 * fazendo a mesma coisa) e voltou a significar só o que o nome diz: ignorar o
 * recorte de PERÍODO — as duas dimensões agora compõem (dá para ver "inativas do
 * período" ou "inativas de sempre"). Por ser o primeiro controle da aba que ENCOLHE
 * o universo de opções dos demais selects, ele criava estado órfão: ver
 * `opcoesComValorSelecionado` no bloco "Opções dos selects".
 *
 * Fonte: `GET /orquestrador/resumo` (1 linha por empresa, RBAC no BFF). Responsável/
 * Área (pedido 08/09, screenshots): a fonte PRIMÁRIA é `linha.areas` do próprio
 * /resumo (cadastro; só as 3 áreas essenciais preenchidas) — cobre empresa em
 * processamento sem execução ativa, que mostrava travessão; fallback no mapa das
 * execuções ativas quando a linha não traz o campo (BFF antigo — degradação nula).
 * As duas células são pilhas verticais PAREADAS (linha N da Área = área do par N
 * do Responsável, ordem fixa contabil→fiscal→pessoal + extras alfabéticas),
 * truncadas por PARES em 2 + "…" — o `title` traz os pares completos
 * ("área: responsável", um por linha).
 */

const POR_PAGINA = 20;

/** Tom da situação — os 4 tones dos KPIs do protótipo. */
const TONE_DA_SITUACAO: Record<SituacaoEmpresa, Tone> = {
  Concluída: 'ok',
  'Em falha': 'err',
  'Em andamento': 'run',
  'Sem registros': 'idle',
};

/** "YYYY-MM-DD HH:MM:SS" local — mesmo formato exibido pelo protótipo. */
const formatarAtividade = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}:${p(d.getSeconds())}`;
};

/** Seta ▲/▼ da coluna ordenada (protótipo, `resumoColunas`). */
const setaOrdenacao = (coluna: ColunaResumo | null, ordenacao: OrdenacaoResumo | null): string => {
  if (!coluna || ordenacao?.coluna !== coluna) return '';
  return ordenacao.asc ? ' ▲' : ' ▼';
};

/**
 * Item das pilhas pareadas Responsável/Área (pedido 08/09): mesma ALTURA de
 * linha nas duas células (o alinhamento par-a-par depende disso) e conteúdo
 * CONTIDO — nowrap + ellipsis + overflow hidden para o texto nunca invadir a
 * célula vizinha (o bug do screenshot); `minWidth: 0` fica no wrapper da célula
 * para o grid poder encolher a coluna.
 */
const ITEM_PILHA_PAR: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  lineHeight: '18px',
};

export interface FiltrosResumoExtras {
  grupoEmpresa: string | null;
  bot: string | null;
  responsavel: string | null;
  area: string | null;
}

interface ResumoTabProps {
  filtros: FiltrosCompartilhados;
  // eslint-disable-next-line no-unused-vars
  onFiltrosChange: (f: FiltrosCompartilhados) => void;
  extras: FiltrosResumoExtras;
  // eslint-disable-next-line no-unused-vars
  onExtrasChange: (e: FiltrosResumoExtras) => void;
  /**
   * Dataset do PERÍODO já recortado pelo regime por nível na fonte (M1 08/09) e
   * AINDA com as inativas/fora da vigência e suas flags — a exclusão global
   * default (index.tsx) NÃO é aplicada aqui (pedido 09/09). É a base única da
   * tabela: quem recorta é o filtro de SITUAÇÃO CADASTRAL, cujo default "Ativas"
   * reproduz exatamente o dataset excluído. Manter as duas listas como props
   * separadas convidaria a divergência que o requisito 4 proíbe (contar de uma
   * fonte e listar de outra), por isso só esta chega à aba.
   */
  empresasComInativas: IEmpresaResumoRecortada[];
  /**
   * Recorte com TODOS os filtros globais — base dos KPIs (nomesAtivos()) enquanto
   * o filtro de situação cadastral está no default. Fora dele, os KPIs passam a
   * derivar do dataset local (ver `contagemKpi`), senão contariam empresa que a
   * tabela não lista (e vice-versa).
   */
  empresasFiltradas: IEmpresaResumoRecortada[];
  responsaveis: IResponsaveisPorEmpresa | undefined;
  vinculos: IVinculoBot[] | undefined;
  // eslint-disable-next-line no-unused-vars
  onIrParaProcessos: (codiEmp: string, regime: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  onIrParaNano: (bot: string, codiEmp: string, regime: string | null) => void;
  /**
   * Período da página — só para a COBERTURA, de onde sai o "falta percorrer"
   * (mesma query e mesma chave que a aba Processos já usa, então o cache é
   * compartilhado). Opcionais: sem eles a cobertura é a do mês corrente.
   */
  params?: IPeriodoParams;
  chavePeriodo?: string;
}

export const ResumoTab: React.FC<ResumoTabProps> = ({
  filtros,
  onFiltrosChange,
  extras,
  onExtrasChange,
  empresasComInativas,
  empresasFiltradas,
  responsaveis,
  vinculos,
  onIrParaProcessos,
  onIrParaNano,
  params,
  chavePeriodo = '',
}) => {
  const [situacaoSel, setSituacaoSel] = useState<SituacaoEmpresa | null>(null);
  const [busca, setBusca] = useState('');
  // Tipo de pesquisa (pedido 08/09): a busca casa SÓ no campo escolhido.
  // Padrão ID desde 19/10 ("é o que mais usamos").
  const [modoBusca, setModoBusca] = useState<ModoBusca>('id');
  // Foco no input realça (sutil) a borda do GRUPO de busca — ver comentário no JSX.
  const [buscaFocada, setBuscaFocada] = useState(false);
  const [ignorarPeriodo, setIgnorarPeriodo] = useState(false);
  // SITUAÇÃO CADASTRAL (pedido 09/09): "ativas" (default) = comportamento de
  // sempre; "inativas" = só as fora das regras; "todas" = sem recorte.
  const [situacaoCadastral, setSituacaoCadastral] = useState<SituacaoCadastral>('ativas');
  const [ordenacao, setOrdenacao] = useState<OrdenacaoResumo | null>(null);
  const [pagina, setPagina] = useState(1);

  // Apelido é SÓ exibição: filtro de bot, chaves e navegação seguem o nome real.
  const { apelidoDoBot, temApelido } = useApelidos();

  // Regime por nível (08/09): o trilho é contexto SEM grupo — vale só o regime
  // PRÓPRIO do bot (nunca herança de cadeia); definido e ≠ regime da empresa,
  // a pílula some do trilho daquela linha. Desde a correção M1, o MESMO recorte
  // é aplicado na FONTE dos datasets (index.tsx e o `empresasTotal` abaixo).
  const regimeProprioDoBot = useMemo(
    () => new Map<string, string | null>((vinculos ?? []).map(v => [v.bot, regimeProprio(v)])),
    [vinculos],
  );

  // Dataset SEM recorte de período: alimenta o banner "X de Y" e o "Ver todas as
  // empresas" — com o MESMO recorte de regime por nível da fonte (M1 08/09), senão
  // esse modo voltaria a casar filtros por bot que o trilho não exibe. Traz o
  // universo COMPLETO (inclusive inativas): quem recorta é `situacaoCadastral`,
  // exatamente como no dataset do período (`empresasComInativas`).
  const { data: resumoTotal } = useQuery({
    queryKey: ['orqResumoTotal'],
    queryFn: () => getResumo({}),
    staleTime: STALE_TEMPO_REAL,
  });
  // COBERTURA: quem a área espera que passe por cada bot. É o que permite dizer
  // "falta percorrer" — o /resumo só conhece bot que já tem log da empresa.
  const { data: cobertura } = useQuery({
    queryKey: ['orqCobertura', chavePeriodo],
    queryFn: () => getCobertura(params ?? {}),
    staleTime: STALE_TEMPO_REAL,
  });
  const esperadosPorEmpresa = useMemo(() => indiceDeBotsEsperados(cobertura?.areas), [cobertura]);
  const areaDoBot = useMemo(() => areaDeCadaBot(cobertura?.areas), [cobertura]);
  // ORDEM DO PROCESSOS (19/10): módulos na ordem dos fluxos que o dono organizou
  // e bots na sequência da esteira — as mesmas queries (e o mesmo cache) da aba.
  const { data: esteira } = useQuery({
    queryKey: ['orqEsteira', chavePeriodo],
    queryFn: () => getEsteira(params ?? {}),
    staleTime: STALE_TEMPO_REAL,
  });
  const { data: departamentos } = useQuery({
    queryKey: ['orqDepDepartamentos'],
    queryFn: () => listarDepartamentos(),
  });
  const ordem = useMemo(() => ordemDoFluxo(esteira, departamentos), [esteira, departamentos]);
  const ordemModulos = useMemo(() => {
    const areas = (cobertura?.areas ?? []).map(a => a.nome);
    const naOrdem = ordem.fluxos.filter(f => areas.includes(f));
    return [...naOrdem, ...areas.filter(a => !naOrdem.includes(a))];
  }, [cobertura, ordem]);
  // MÓDULOS (19/10): multi-escolha; vazio = todos. Recorta o percurso de cada
  // cartão E a lista (só fica quem participa de algum módulo escolhido).
  const [modulosSel, setModulosSel] = useState<string[]>([]);
  const [soComFalta, setSoComFalta] = useState(false);
  const [modalBot, setModalBot] = useState<AlvoModalBot | null>(null);

  const empresasTotal = useMemo(
    () => aplicarRegimeProprioNoResumo(resumoTotal?.empresas ?? [], regimeProprioDoBot),
    [resumoTotal, regimeProprioDoBot],
  );

  const filtrosCompletos: FiltrosResumo = useMemo(
    () => ({
      ...FILTROS_RESUMO_VAZIOS,
      busca,
      buscaModo: modoBusca,
      regime: filtros.regime,
      empresa: filtros.empresa,
      // Filtro de STATUS (pedido 04/09): ALGUM bot da rota num status selecionado,
      // em E lógico com os demais — inclusive com o KPI de situação clicável.
      status: filtros.status,
      grupoEmpresa: extras.grupoEmpresa,
      bot: extras.bot,
      responsavel: extras.responsavel,
      area: extras.area,
      // SITUAÇÃO CADASTRAL (pedido 09/09): compõe em E lógico com todo o resto —
      // é a ÚNICA porta das inativas, e o "Ver todas as empresas" (período) não
      // mexe mais nela. Ver o comentário do JSX do banner.
      situacaoCadastral,
    }),
    [busca, modoBusca, filtros.regime, filtros.empresa, filtros.status, extras, situacaoCadastral],
  );

  // Dataset base da aba: o do PERÍODO ou o TOTAL ("Ver todas as empresas"), sempre
  // COM as inativas — o recorte por situação cadastral acontece uma única vez,
  // dentro de `filtrarEmpresasResumo`, para tabela/KPIs/contadores lerem o MESMO
  // conjunto (nunca contar uma coisa e listar outra).
  const dataset = ignorarPeriodo ? empresasTotal : empresasComInativas;

  const porModuloDe = useMemo(() => {
    const cache = new Map<string, PercursoPorModulo>();
    return (e: IEmpresaResumoRecortada): PercursoPorModulo => {
      let r = cache.get(e.codi_emp);
      if (!r) {
        r = percursoPorModulo(
          e.bots,
          esperadosPorEmpresa.get(e.codi_emp),
          areaDoBot,
          ordemModulos,
          e.empresa?.regime ?? null,
          regimeProprioDoBot,
          modulosSel,
        );
        cache.set(e.codi_emp, r);
      }
      return r;
    };
  }, [esperadosPorEmpresa, areaDoBot, ordemModulos, regimeProprioDoBot, modulosSel]);

  /** Recorte de módulo/falta: só vale com a cobertura na mão (sem ela não há módulo). */
  const recortaModulo = Boolean(cobertura) && (modulosSel.length > 0 || soComFalta);
  const passaModulo = useCallback(
    (e: IEmpresaResumoRecortada) => {
      if (!recortaModulo) return true;
      const r = porModuloDe(e);
      if (!r.modulos.length) return false;
      return !soComFalta || r.total.faltam.length > 0;
    },
    [recortaModulo, porModuloDe, soComFalta],
  );

  const linhasBase = useMemo(() => {
    const filtradas = filtrarEmpresasResumo(dataset, filtrosCompletos, responsaveis).filter(
      passaModulo,
    );
    const comSituacao = situacaoSel ? filtradas.filter(e => e.situacao === situacaoSel) : filtradas;
    return ordenarEmpresasResumo(comSituacao, ordenacao);
  }, [dataset, filtrosCompletos, responsaveis, situacaoSel, ordenacao, passaModulo]);

  const totalPaginas = Math.max(1, Math.ceil(linhasBase.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const linhasDaPagina = linhasBase.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);

  // KPIs: contagem sobre o recorte global (nomesAtivos() do protótipo — sem busca
  // nem o próprio KPI selecionado). No default ("Ativas" + período) é o
  // `empresasFiltradas` da página, como sempre foi. Assim que a situação cadastral
  // ou o período saem do default, o recorte global da página deixa de descrever o
  // conjunto listado (ele nunca vê inativas) — o KPI passa então a derivar do
  // MESMO `dataset` da tabela, com os mesmos filtros menos a busca. Contar de uma
  // fonte e listar de outra é o bug que este recorte evita.
  const kpiSegueAPagina = !ignorarPeriodo && situacaoCadastral === 'ativas' && !recortaModulo;
  const baseKpi = useMemo(
    () =>
      kpiSegueAPagina
        ? empresasFiltradas
        : filtrarEmpresasResumo(dataset, { ...filtrosCompletos, busca: '' }, responsaveis).filter(
            passaModulo,
          ),
    [kpiSegueAPagina, empresasFiltradas, dataset, filtrosCompletos, responsaveis, passaModulo],
  );
  const contagemKpi = useMemo(() => {
    const mapa = new Map<SituacaoEmpresa, number>();
    baseKpi.forEach(e => mapa.set(e.situacao, (mapa.get(e.situacao) ?? 0) + 1));
    return mapa;
  }, [baseKpi]);

  // Banner: X = o que o recorte de PERÍODO mostra; Y = o universo sem período —
  // ambos já sob a MESMA situação cadastral escolhida (senão o "X de Y" mediria
  // duas populações diferentes e o "Ver todas" prometeria empresas que o filtro
  // de situação não deixaria aparecer).
  const filtrosDoBanner = useMemo(() => ({ ...filtrosCompletos, busca: '' }), [filtrosCompletos]);
  const totalComPeriodo = useMemo(
    () => filtrarEmpresasResumo(empresasComInativas, filtrosDoBanner, responsaveis).length,
    [empresasComInativas, filtrosDoBanner, responsaveis],
  );
  const totalGeral = useMemo(
    () => filtrarEmpresasResumo(empresasTotal, filtrosDoBanner, responsaveis).length,
    [empresasTotal, filtrosDoBanner, responsaveis],
  );
  const periodoTemOcultas = !ignorarPeriodo && totalComPeriodo < totalGeral;
  const periodoInfo = ignorarPeriodo
    ? `Mostrando todas as ${totalGeral} empresas — sem recorte de período.`
    : `Para este período foram encontradas ${totalComPeriodo} de ${totalGeral} empresas com última atividade registrada.`;

  // ── Opções dos selects ─────────────────────────────────────────────────────
  // Regime/Grupo/Responsável/Área derivam do conjunto VISÍVEL no modo atual
  // (achado da revisão 08/09): um valor que só existe em empresa que o filtro de
  // situação cadastral esconde viraria opção de tabela vazia sem explicação. Com
  // a reforma de 09/09 quem define o conjunto visível é `situacaoCadastral`, então
  // as opções seguem o MESMO recorte da tabela — em "Inativas", só o que existe
  // nas inativas; em "Todas", tudo.
  //
  // Estado ÓRFÃO (achado MÉDIO da revisão 09/09): como este recorte ENCOLHE o
  // universo de opções, um valor já escolhido (ex.: regime "Simples Nacional") pode
  // deixar de existir ao trocar para "Inativas" — a option sumia, o `<select>`
  // controlado exibia o placeholder e MENTIA, com o filtro ainda aplicado zerando a
  // tabela. As 4 dimensões passam por `opcoesComValorSelecionado`, que mantém o
  // valor visível com o sufixo "(sem empresas neste recorte)". Limpar em silêncio
  // não serve: regime/empresa são estado COMPARTILHADO com a aba Processos.
  const baseOpcoes = useMemo(
    () => filtrarPorSituacaoCadastral(dataset, situacaoCadastral),
    [dataset, situacaoCadastral],
  );
  const regimes = useMemo(() => regimesDisponiveis(baseOpcoes), [baseOpcoes]);
  const gruposDeEmpresa = useMemo(
    () =>
      Array.from(
        new Set(
          baseOpcoes.map(e => e.empresa?.grupo_de_empresas).filter((g): g is string => Boolean(g)),
        ),
      ).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [baseOpcoes],
  );
  // Value = nome REAL (é o que filtra); label = apelido, ordenado pelo que se lê.
  const opcoesBot = useMemo(
    () =>
      (vinculos ?? [])
        .map(v => ({ value: v.bot, label: apelidoDoBot(v.bot) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [vinculos, apelidoDoBot],
  );
  // Responsável/Área (pedido 08/09): opções da UNIÃO fonte-primária (areas por
  // linha, do cadastro) + fallback (mapa das execuções) — mesmo universo que
  // `batemResponsaveis` casa, para filtrar também empresa que só tem cadastro.
  const opcoesResponsavel = useMemo(
    () => opcoesDeResponsaveis(baseOpcoes, responsaveis),
    [baseOpcoes, responsaveis],
  );
  const opcoesArea = useMemo(
    () => opcoesDeAreas(baseOpcoes, responsaveis),
    [baseOpcoes, responsaveis],
  );

  const depDoBot = useMemo(
    () => new Map<string, string | null>((vinculos ?? []).map(v => [v.bot, v.dep_bot])),
    [vinculos],
  );

  const temFiltroAtivo = Boolean(
    filtros.regime ||
      filtros.empresa ||
      filtros.status.length ||
      extras.grupoEmpresa ||
      extras.bot ||
      extras.responsavel ||
      extras.area ||
      busca ||
      situacaoSel ||
      modulosSel.length ||
      soComFalta ||
      // "Ativas" é o default: só conta como filtro ativo fora dele.
      situacaoCadastral !== 'ativas',
  );

  const limparFiltros = () => {
    onFiltrosChange({ ...filtros, regime: null, empresa: null, status: [] });
    onExtrasChange({ grupoEmpresa: null, bot: null, responsavel: null, area: null });
    setBusca('');
    setSituacaoSel(null);
    setSituacaoCadastral('ativas');
    setModulosSel([]);
    setSoComFalta(false);
    setPagina(1);
  };

  const resetPagina = () => setPagina(1);

  const ordenarPor = (coluna: ColunaResumo) =>
    setOrdenacao(atual =>
      atual && atual.coluna === coluna ? { coluna, asc: !atual.asc } : { coluna, asc: true },
    );

  const colunas: Array<{ key: ColunaResumo; label: string }> = [
    { key: 'empresa', label: 'Empresa' },
    { key: 'grupo', label: 'Grupo' },
    { key: 'regime', label: 'Regime' },
    { key: 'bots', label: 'Bots' },
    { key: 'atividade', label: 'Última atividade' },
  ];

  // Rodapé no formato de INTERVALO, igual ao da aba Erros (achado 7 da auditoria
  // 09/09). Antes dizia "Mostrando 20 de 47", onde o 20 era o TAMANHO da página —
  // lado a lado com o "Mostrando 1–50 de 108" de Erros, os dois rodapés pareciam
  // medir a mesma coisa e não mediam. A aritmética já existia (paginaAtual e
  // POR_PAGINA); só o texto muda, nenhum número novo. Lista vazia → "0–0 de 0",
  // nunca "1–0".
  const inicioLinha = linhasBase.length === 0 ? 0 : (paginaAtual - 1) * POR_PAGINA + 1;
  const fimLinha = (paginaAtual - 1) * POR_PAGINA + linhasDaPagina.length;
  const footer = `Mostrando ${inicioLinha}–${fimLinha} de ${linhasBase.length} empresa${
    linhasBase.length === 1 ? '' : 's'
  } no filtro atual (página ${paginaAtual} de ${totalPaginas}). Clique num cartão para ver a esteira desta empresa em Processos.`;

  const selectResumoStyle: React.CSSProperties = {
    padding: '7px 30px 7px 12px',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.95)',
    background: 'rgba(255,255,255,0.72)',
  };

  const totalKpi = baseKpi.length;

  return (
    <div style={fundoDaAba('resumo')}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))',
          gap: '14px',
        }}
      >
        {SITUACOES_KPI.map(kpi => {
          const tone = TONE_DA_SITUACAO[kpi.situacao];
          const t = TONES[tone];
          const ativo = situacaoSel === kpi.situacao;
          const qtd = contagemKpi.get(kpi.situacao) ?? 0;
          const pct = totalKpi ? (qtd / totalKpi) * 100 : 0;
          return (
            <div
              key={kpi.situacao}
              className="orq-elevavel"
              onClick={() => {
                setSituacaoSel(atual => (atual === kpi.situacao ? null : kpi.situacao));
                resetPagina();
              }}
              style={{
                ...CARTAO,
                padding: '16px 18px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                outline: ativo ? `2px solid ${t.dot}` : 'none',
                outlineOffset: '-2px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '7px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <span
                    style={{
                      width: '9px',
                      height: '9px',
                      borderRadius: '50%',
                      background: t.dot,
                      boxShadow: `0 0 0 4px color-mix(in oklch, ${t.dot} 18%, transparent)`,
                      flex: 'none',
                    }}
                  />
                  <span
                    style={{
                      fontFamily: FONTE_MONO,
                      fontSize: '10px',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: t.fg,
                      fontWeight: 600,
                    }}
                  >
                    {kpi.label}
                  </span>
                </div>
                {ativo && <span style={{ fontSize: '11px', color: t.fg }}>✕</span>}
              </div>
              <div
                style={{
                  fontSize: '30px',
                  fontWeight: 700,
                  letterSpacing: '-0.03em',
                  color: C.ink,
                }}
              >
                {qtd}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    flex: 1,
                    height: '6px',
                    borderRadius: '999px',
                    background: 'rgba(16,60,48,0.08)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      borderRadius: '999px',
                      background: `linear-gradient(90deg, color-mix(in oklch, ${t.dot} 70%, white), ${t.dot})`,
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
                <span style={{ fontFamily: FONTE_MONO, fontSize: '10.5px', color: C.sub }}>
                  {textoPct(qtd, totalKpi)} do total
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...CARTAO, display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <TituloSecao
          aba="resumo"
          icone={<Icone nome="empresa" tamanho={17} />}
          titulo="Empresas"
          sub="Cada empresa com os bots que ela já percorreu, os que deram erro ou aviso e os que ela ainda precisa percorrer."
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Busca UNIFICADA (pedido 08/09): seletor de TIPO + textbox num controle
              único. Desde 19/10 o tipo padrão é ID ("é o que mais usamos").
              Trocar o tipo LIMPA o termo (o termo de um campo quase nunca casa no
              outro — mantê-lo zerava o resultado em silêncio). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              border: `1px solid ${buscaFocada ? 'oklch(0.62 0.13 240)' : 'rgba(255,255,255,0.95)'}`,
              boxShadow: buscaFocada ? '0 0 0 3px oklch(0.62 0.13 240 / 0.15)' : 'none',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.78)',
              overflow: 'hidden',
              transition: 'box-shadow 0.15s ease',
            }}
          >
            <SelectFiltro
              value={modoBusca}
              onChange={novo => {
                const transicao = aoTrocarModoBusca(novo);
                setModoBusca(transicao.modo);
                setBusca(transicao.busca);
                resetPagina();
              }}
              opcoes={[
                { value: 'id', label: 'ID' },
                { value: 'razao', label: 'Razão social' },
              ]}
              wrapperStyle={{ borderRight: '1px solid oklch(0.9 0.006 160)' }}
              selectStyle={{
                ...selectResumoStyle,
                border: 'none',
                borderRadius: '12px 0 0 12px',
                background: 'transparent',
                fontWeight: 600,
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 12px' }}>
              <Icone nome="busca" tamanho={15} style={{ color: C.faint }} />
              <input
                type="text"
                placeholder={modoBusca === 'id' ? 'Buscar código' : 'Buscar razão social'}
                value={busca}
                onChange={ev => {
                  setBusca(ev.target.value);
                  resetPagina();
                }}
                onFocus={() => setBuscaFocada(true)}
                onBlur={() => setBuscaFocada(false)}
                style={{
                  border: 'none',
                  outline: 'none',
                  fontFamily: FONTE_TEXTO,
                  fontSize: '12.5px',
                  color: 'oklch(0.3 0.02 165)',
                  width: '170px',
                  background: 'transparent',
                }}
              />
            </div>
          </div>
          {/* SITUAÇÃO CADASTRAL (pedido 09/09) — "Empresas ativas" (default) |
              "Empresas inativas" | "Ativas e inativas". É a forma CANÔNICA de
              revelar inativas: o "Ver todas as empresas" ignora só o PERÍODO. */}
          <SelectFiltro
            value={situacaoCadastral}
            onChange={novo => {
              setSituacaoCadastral(novo as SituacaoCadastral);
              resetPagina();
            }}
            opcoes={SITUACOES_CADASTRAIS.map(s => ({ value: s.value, label: s.label }))}
            selectStyle={selectResumoStyle}
          />
          <SelectFiltro
            value={filtros.regime ?? ''}
            onChange={novo => {
              onFiltrosChange(aoEscolherRegime(filtros, novo || null));
              resetPagina();
            }}
            opcoes={opcoesComValorSelecionado(
              [
                { value: '', label: 'Todos os regimes' },
                ...regimes.map(r => ({ value: r, label: r })),
              ],
              filtros.regime ?? '',
            )}
            selectStyle={selectResumoStyle}
          />
          <SelectFiltro
            value={extras.grupoEmpresa ?? ''}
            onChange={novo => {
              onExtrasChange({ ...extras, grupoEmpresa: novo || null });
              resetPagina();
            }}
            opcoes={opcoesComValorSelecionado(
              [
                { value: '', label: 'Todos os grupos' },
                ...gruposDeEmpresa.map(g => ({ value: g, label: g })),
              ],
              extras.grupoEmpresa ?? '',
            )}
            selectStyle={{ ...selectResumoStyle, maxWidth: '200px' }}
          />
          {/* O select "Todas as empresas" SAIU do Resumo (pedido 08/09) — a busca
              por ID cobre o caso; em Processos ele permanece. */}
          <SelectFiltro
            value={extras.bot ?? ''}
            onChange={novo => {
              onExtrasChange({ ...extras, bot: novo || null });
              resetPagina();
            }}
            opcoes={[{ value: '', label: 'Todos os bots' }, ...opcoesBot]}
            selectStyle={{ ...selectResumoStyle, maxWidth: '200px' }}
          />
          <SelectFiltro
            value={extras.responsavel ?? ''}
            onChange={novo => {
              onExtrasChange({ ...extras, responsavel: novo || null });
              resetPagina();
            }}
            opcoes={opcoesComValorSelecionado(
              [
                { value: '', label: 'Todos os responsáveis' },
                ...opcoesResponsavel.map(r => ({ value: r, label: r })),
              ],
              extras.responsavel ?? '',
            )}
            selectStyle={{ ...selectResumoStyle, maxWidth: '200px' }}
          />
          <SelectFiltro
            value={extras.area ?? ''}
            onChange={novo => {
              onExtrasChange({ ...extras, area: novo || null });
              resetPagina();
            }}
            opcoes={opcoesComValorSelecionado(
              [
                { value: '', label: 'Todas as áreas' },
                ...opcoesArea.map(a => ({ value: a, label: a })),
              ],
              extras.area ?? '',
            )}
            selectStyle={{ ...selectResumoStyle, maxWidth: '180px' }}
          />
          {temFiltroAtivo && (
            <div
              onClick={limparFiltros}
              title="Limpar filtros"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '10px',
                border: `1px solid color-mix(in oklch, ${C.red} 30%, transparent)`,
                background: 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                color: C.redFg,
                fontSize: '13px',
              }}
            >
              ✕
            </div>
          )}
        </div>

        {/* Pílulas de STATUS em linha própria, ABAIXO dos filtros gerais (pedido
            08/09) — em Processos elas continuam onde estão. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px 18px',
            flexWrap: 'wrap',
          }}
        >
          <FiltroStatus
            selecionados={filtros.status}
            onToggle={st => {
              onFiltrosChange(aoAlternarStatus(filtros, st));
              resetPagina();
            }}
            onLimpar={() => {
              onFiltrosChange({ ...filtros, status: [] });
              resetPagina();
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={EYEBROW}>Ordenar</span>
            {colunas.map(col => {
              const ativa = ordenacao?.coluna === col.key;
              return (
                <div
                  key={col.key}
                  onClick={() => ordenarPor(col.key)}
                  style={{
                    ...estiloPilula(ativa, 'resumo'),
                    padding: '4px 11px',
                    fontSize: '11.5px',
                  }}
                >
                  {col.label}
                  {setaOrdenacao(col.key, ordenacao)}
                </div>
              );
            })}
          </div>
        </div>

        {/* Banner do PERÍODO — contador "X de Y" preservado. Desde 09/09 o "Ver
            todas as empresas" ignora SÓ o recorte de período (quem revela inativa
            é o filtro de situação cadastral): os dois números já saem da mesma
            situação cadastral escolhida, então o "X de Y" nunca compara
            populações diferentes. */}
        <Faixa tom="run" style={{ alignItems: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <div>{periodoInfo}</div>
            {periodoTemOcultas && (
              <div
                onClick={() => {
                  setIgnorarPeriodo(true);
                  resetPagina();
                }}
                style={{ fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Ver todas as empresas →
              </div>
            )}
            {ignorarPeriodo && (
              <div
                onClick={() => {
                  setIgnorarPeriodo(false);
                  resetPagina();
                }}
                style={{ fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Voltar a filtrar pelo período
              </div>
            )}
          </div>
        </Faixa>

        {ordemModulos.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={EYEBROW}>Módulos</span>
            <div
              onClick={() => {
                setModulosSel([]);
                resetPagina();
              }}
              style={{
                ...estiloPilula(!modulosSel.length, 'resumo'),
                padding: '4px 11px',
                fontSize: '11.5px',
              }}
            >
              Todos
            </div>
            {ordemModulos.map(m => {
              const ativo = modulosSel.includes(m);
              return (
                <div
                  key={m}
                  role="button"
                  tabIndex={0}
                  aria-pressed={ativo}
                  onClick={() => {
                    setModulosSel(atual =>
                      atual.includes(m) ? atual.filter(x => x !== m) : [...atual, m],
                    );
                    resetPagina();
                  }}
                  style={{
                    ...estiloPilula(ativo, 'resumo'),
                    padding: '4px 11px',
                    fontSize: '11.5px',
                  }}
                >
                  {ativo ? '✓ ' : ''}
                  {m}
                </div>
              );
            })}
            <span style={{ width: '1px', height: '18px', background: 'rgba(16,60,48,0.12)' }} />
            <div
              role="button"
              tabIndex={0}
              aria-pressed={soComFalta}
              onClick={() => {
                setSoComFalta(v => !v);
                resetPagina();
              }}
              title="Só as empresas com algum bot esperado e ainda sem log, nos módulos escolhidos"
              style={{
                ...estiloPilula(soComFalta, 'erros'),
                padding: '4px 11px',
                fontSize: '11.5px',
              }}
            >
              Só com falta para percorrer
            </div>
          </div>
        )}

        <LegendaPercurso />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 440px), 1fr))',
          gap: '14px',
        }}
      >
        {linhasDaPagina.map(e => (
          <CartaoEmpresa
            key={e.codi_emp}
            e={e}
            responsaveis={responsaveis}
            porModulo={porModuloDe(e)}
            posicaoDoBot={ordem.posicaoDoBot}
            coberturaCarregada={Boolean(cobertura)}
            depDoBot={depDoBot}
            regimeProprioDoBot={regimeProprioDoBot}
            apelidoDoBot={apelidoDoBot}
            temApelido={temApelido}
            onIrParaProcessos={onIrParaProcessos}
            onAbrirBot={pedido =>
              setModalBot({
                ...pedido,
                codiEmp: e.codi_emp,
                nomeEmpresa: nomeEmpresa(e),
                regime: e.empresa?.regime ?? null,
              })
            }
          />
        ))}
      </div>

      {/* Estado VAZIO honesto (achado MÉDIO da revisão 09/09): diz que nenhum
          registro casa os filtros atuais, sem sugerir qual filtro afrouxar. */}
      {!linhasDaPagina.length && (
        <div
          style={
            {
              ...CARTAO,
              padding: '34px 22px',
              textAlign: 'center',
              fontSize: '12.5px',
              color: C.sub,
              textWrap: 'pretty',
            } as React.CSSProperties
          }
        >
          Nenhuma empresa corresponde aos filtros atuais.
        </div>
      )}

      {modalBot && (
        <ModalBotDaEmpresa
          alvo={modalBot}
          apelidoDoBot={apelidoDoBot}
          de={params?.date_init}
          ate={params?.date_end}
          onFechar={() => setModalBot(null)}
          onAbrirNoProcessos={() => {
            const alvo = modalBot;
            setModalBot(null);
            onIrParaNano(alvo.bot, alvo.codiEmp, alvo.regime);
          }}
        />
      )}

      <div
        style={{
          ...CARTAO,
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: '12.5px', color: C.sub }}>{footer}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            onClick={() => paginaAtual > 1 && setPagina(paginaAtual - 1)}
            style={{
              ...estiloPilula(false, 'resumo'),
              color: paginaAtual > 1 ? C.ink : 'oklch(0.78 0.01 165)',
              cursor: paginaAtual > 1 ? 'pointer' : 'default',
            }}
          >
            ← Anterior
          </div>
          <div
            style={{
              fontFamily: FONTE_MONO,
              fontSize: '11.5px',
              color: C.sub,
              whiteSpace: 'nowrap',
            }}
          >
            Página {paginaAtual} de {totalPaginas}
          </div>
          <div
            onClick={() => paginaAtual < totalPaginas && setPagina(paginaAtual + 1)}
            style={{
              ...estiloPilula(false, 'resumo'),
              color: paginaAtual < totalPaginas ? C.ink : 'oklch(0.78 0.01 165)',
              cursor: paginaAtual < totalPaginas ? 'pointer' : 'default',
            }}
          >
            Próxima →
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CARTÃO DA EMPRESA
// ═══════════════════════════════════════════════════════════════════════════

/** Glifo de cada status nas pílulas — o mesmo significado das cores, sem depender delas. */
const GLIFO_DO_STATUS: Record<StatusEsteira, string> = {
  done: '✓',
  err: '✕',
  warn: '!',
  run: '◌',
  wait: '◷',
  skip: '–',
};

/**
 * Rótulo da situação NO CARTÃO. Não repete o texto dos KPIs ("Em falha",
 * "Concluídas"…) de propósito: o KPI é encontrado pelo rótulo, e o mesmo texto
 * em 20 cartões tornaria a busca ambígua para leitor de tela e para o teste.
 */
const SITUACAO_NO_CARTAO: Record<SituacaoEmpresa, string> = {
  Concluída: 'Rota concluída',
  'Em falha': 'Com falha',
  'Em andamento': 'Rodando',
  'Sem registros': 'Sem log',
};

/** Rótulos da legenda — próprios, para não repetir os das pílulas de filtro. */
const ROTULO_DA_LEGENDA: Partial<Record<StatusEsteira, string>> = {
  done: 'concluiu',
  err: 'erro',
  warn: 'aviso',
  run: 'rodando',
  skip: 'pendente (não conta como concluído)',
};

const LegendaPercurso: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px 14px',
      flexWrap: 'wrap',
      fontSize: '11px',
      color: C.sub,
    }}
  >
    <span style={EYEBROW}>Legenda</span>
    {(['done', 'err', 'warn', 'run', 'skip'] as StatusEsteira[]).map(st => {
      const t = TONES[TONE_DO_STATUS[st]];
      return (
        <span key={st} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <span
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '5px',
              background: t.bg,
              color: t.fg,
              fontSize: '10px',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {GLIFO_DO_STATUS[st]}
          </span>
          {ROTULO_DA_LEGENDA[st]}
        </span>
      );
    })}
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <span
        style={{
          width: '14px',
          height: '14px',
          borderRadius: '5px',
          border: `1.5px dashed ${C.faint}`,
        }}
      />
      Falta percorrer
    </span>
  </div>
);

/**
 * Anel de progresso: cada fatia é uma categoria do percurso, na ordem
 * concluído → aviso → erro → andamento → pendente → falta. O número do centro
 * é só o CONCLUÍDO sobre o esperado (mesma régua do painel do Processos).
 */
const AnelPercurso: React.FC<{ percurso: PercursoDaEmpresa }> = ({ percurso }) => {
  const r = 22;
  const circ = 2 * Math.PI * r;
  const fatias: Array<{ n: number; cor: string }> = [
    { n: percurso.concluidos.length, cor: C.green },
    { n: percurso.avisos.length, cor: C.amber },
    { n: percurso.erros.length, cor: C.red },
    { n: percurso.andamento.length, cor: C.blue },
    { n: percurso.pendentes.length, cor: 'oklch(0.8 0.01 165)' },
  ];
  const total = Math.max(1, percurso.total);
  let acumulado = 0;
  const pct = percurso.pctConcluido;
  let corCentro: string = C.sub;
  if (pct !== null) {
    if (pct >= 100) corCentro = C.greenFg;
    else if (pct >= 75) corCentro = C.blueFg;
    else if (pct >= 40) corCentro = C.amberFg;
    else corCentro = C.redFg;
  }
  return (
    <div style={{ position: 'relative', width: '56px', height: '56px', flex: 'none' }}>
      <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden>
        <circle cx="28" cy="28" r={r} fill="none" stroke="rgba(16,60,48,0.08)" strokeWidth="6" />
        {fatias.map((f, i) => {
          if (!f.n) return null;
          const tam = (f.n / total) * circ;
          const el = (
            <circle
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              cx="28"
              cy="28"
              r={r}
              fill="none"
              stroke={f.cor}
              strokeWidth="6"
              strokeDasharray={`${tam} ${circ - tam}`}
              strokeDashoffset={-acumulado}
              transform="rotate(-90 28 28)"
            />
          );
          acumulado += tam;
          return el;
        })}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONTE_MONO,
          fontSize: '11px',
          fontWeight: 700,
          color: corCentro,
        }}
      >
        {pct === null ? '—' : `${Math.round(pct)}%`}
      </div>
    </div>
  );
};

/** O que o cartão pede para abrir no modal (bot percorrido ou faltante). */
export interface PedidoModalBot {
  bot: string;
  info: IBotDaEmpresa | null;
  area?: string | null;
}

/**
 * Quadradinho do MAPA DE BOTS (19/10: "ficou muito grande a lista de bots").
 * Um bot = um quadrado de 22px com o glifo do status; o nome vai no `title` e
 * na lista expandida. 30 bots cabem em uma ou duas linhas em vez de seis.
 */
const QuadroBot: React.FC<{
  rotulo: string;
  status: StatusEsteira | null;
  onClick: () => void;
}> = ({ rotulo, status, onClick }) => {
  const tb = status ? TONES[TONE_DO_STATUS[status] ?? 'idle'] : null;
  return (
    <button
      type="button"
      onClick={ev => {
        ev.stopPropagation();
        onClick();
      }}
      title={`${rotulo} — ${status ? (ST_META[status]?.label ?? status) : 'falta percorrer'}. Clique para ver o erro e o histórico.`}
      aria-label={rotulo}
      className="orq-quadro-bot"
      style={{
        width: '22px',
        height: '22px',
        padding: 0,
        borderRadius: '7px',
        border: tb ? 'none' : `1.5px dashed oklch(0.74 0.01 165)`,
        background: tb ? tb.dot : 'rgba(255,255,255,0.55)',
        color: tb ? 'white' : C.faint,
        fontSize: '10.5px',
        fontWeight: 700,
        fontFamily: FONTE_TEXTO,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxSizing: 'border-box',
        flex: 'none',
      }}
    >
      {status ? GLIFO_DO_STATUS[status] : ''}
    </button>
  );
};

const CartaoEmpresa: React.FC<{
  e: IEmpresaResumoRecortada;
  responsaveis: IResponsaveisPorEmpresa | undefined;
  /** Percurso já separado por módulo (e recortado pelo filtro de módulos). */
  porModulo: PercursoPorModulo;
  /** Posição do bot na esteira do Processos (fluxo → grupo → bot). */
  posicaoDoBot: Map<string, number>;
  coberturaCarregada: boolean;
  depDoBot: Map<string, string | null>;
  regimeProprioDoBot: Map<string, string | null>;
  // eslint-disable-next-line no-unused-vars
  apelidoDoBot: (bot: string) => string;
  // eslint-disable-next-line no-unused-vars
  temApelido: (bot: string) => boolean;
  // eslint-disable-next-line no-unused-vars
  onIrParaProcessos: (codiEmp: string, regime: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  onAbrirBot: (pedido: PedidoModalBot) => void;
}> = ({
  e,
  responsaveis,
  porModulo,
  posicaoDoBot,
  coberturaCarregada,
  depDoBot,
  regimeProprioDoBot,
  apelidoDoBot,
  temApelido,
  onIrParaProcessos,
  onAbrirBot,
}) => {
  const [comNomes, setComNomes] = useState(false);
  const semLogs = e.situacao === 'Sem registros';
  // Rota INTEIRA ocultada pelo recorte de regime por nível (M1 08/09): o cartão
  // explica em cinza — nunca vazio sem explicação (nem a mensagem de "nenhum
  // registro", que seria mentira: registro há, só está oculto pela aba
  // Dependências).
  const rotaOcultaPeloRegime = e.bots.length === 0 && (e.botsOcultosPorRegime ?? 0) > 0;
  const regime = e.empresa?.regime ?? null;
  const pares = paresAreaResponsavel(areasDaLinha(e, responsaveis));
  const { visiveis: paresVisiveis, truncado: paresTruncados } = resumirLista(pares);
  const tituloPares = tooltipDosPares(pares) || undefined;
  const percurso = porModulo.total;
  const trilhos = useMemo(
    () => trilhosDaEmpresa(e, depDoBot, regimeProprioDoBot),
    [e, depDoBot, regimeProprioDoBot],
  );
  // Ordem do trilho (dependência) dentro de cada módulo.
  const ordemNoTrilho = useMemo(() => {
    const m = new Map<string, number>();
    trilhos.flat().forEach((b, i) => m.set(b.bot, i));
    return m;
  }, [trilhos]);
  // Ordem do Processos primeiro; quem não está na esteira cai na ordem do trilho.
  const posicao = (bot: string) =>
    posicaoDoBot.get(bot) ?? 100000 + (ordemNoTrilho.get(bot) ?? 999);
  const botsDoModulo = (p: PercursoDaEmpresa): IBotDaEmpresa[] =>
    [...p.concluidos, ...p.erros, ...p.avisos, ...p.andamento, ...p.pendentes].sort(
      (a, b) => posicao(a.bot) - posicao(b.bot),
    );
  const faltamNaOrdem = (p: PercursoDaEmpresa) =>
    [...p.faltam].sort((a, b) => posicao(a.bot) - posicao(b.bot));
  const t = TONES[TONE_DA_SITUACAO[e.situacao]];
  const problemas = [...percurso.erros, ...percurso.avisos];

  const pilula = (b: IBotDaEmpresa) => {
    const tb = TONES[TONE_DO_STATUS[b.status] ?? 'idle'];
    return (
      <div
        key={b.bot}
        onClick={ev => {
          ev.stopPropagation();
          onAbrirBot({ bot: b.bot, info: b });
        }}
        title={`${temApelido(b.bot) ? `${b.bot} · ` : ''}${
          ST_META[b.status]?.label ?? ''
        } — clique para ver o erro e o histórico`}
        style={{
          padding: '2px 7px 2px 3px',
          borderRadius: '7px',
          background: tb.bg,
          color: tb.fg,
          fontSize: '10.5px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            width: '13px',
            height: '13px',
            borderRadius: '4px',
            background: tb.dot,
            color: 'white',
            fontSize: '8.5px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          {GLIFO_DO_STATUS[b.status]}
        </span>
        {apelidoDoBot(b.bot)}
      </div>
    );
  };

  let corpo: React.ReactNode;
  if (rotaOcultaPeloRegime) {
    corpo = (
      <div
        title="Todos os bots da rota desta empresa estão ocultos pelo regime configurado na aba Dependências"
        style={{ fontSize: '11.5px', fontStyle: 'italic', color: C.faint }}
      >
        oculto pelo regime
      </div>
    );
  } else if (semLogs) {
    corpo = (
      <div style={{ fontSize: '11.5px', color: C.sub, textWrap: 'pretty' } as React.CSSProperties}>
        Nenhum registro de bot encontrado para esta empresa
      </div>
    );
  } else if (!porModulo.modulos.length) {
    corpo = (
      <div style={{ fontSize: '11.5px', color: C.faint }}>
        Esta empresa não participa dos módulos escolhidos.
      </div>
    );
  } else {
    // UMA LINHA POR MÓDULO (19/10): o nome do módulo, os bots dele e o "x de y".
    // Compacto = quadradinhos; "ver nomes" = pílulas com o nome. Faltantes no fim
    // da linha, tracejados.
    corpo = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {porModulo.modulos.map(({ modulo, percurso: p }) => (
          <div
            key={modulo}
            style={{ display: 'flex', alignItems: comNomes ? 'flex-start' : 'center', gap: '8px' }}
          >
            <div
              title={modulo}
              style={{
                width: '104px',
                flex: 'none',
                fontSize: '10.5px',
                fontWeight: 650,
                color: 'oklch(0.42 0.03 165)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                paddingTop: comNomes ? '3px' : 0,
              }}
            >
              {modulo}
              <span
                style={{
                  display: 'block',
                  fontFamily: FONTE_MONO,
                  fontSize: '9.5px',
                  fontWeight: 500,
                  color: p.faltam.length ? C.redFg : C.faint,
                }}
              >
                {`${p.concluidos.length}/${p.total} ok`}
                {p.faltam.length ? ` · ${p.faltam.length} falta` : ''}
              </span>
            </div>
            <div
              style={{ display: 'flex', gap: comNomes ? '3px' : '4px', flexWrap: 'wrap', flex: 1 }}
            >
              {botsDoModulo(p).map(b =>
                comNomes ? (
                  pilula(b)
                ) : (
                  <QuadroBot
                    key={b.bot}
                    rotulo={apelidoDoBot(b.bot)}
                    status={b.status}
                    onClick={() => onAbrirBot({ bot: b.bot, info: b })}
                  />
                ),
              )}
              {faltamNaOrdem(p).map(x =>
                comNomes ? (
                  <div
                    key={x.bot}
                    onClick={ev => {
                      ev.stopPropagation();
                      onAbrirBot({ bot: x.bot, info: null, area: x.area });
                    }}
                    title={`Esperado pela área ${x.area}, sem log desta empresa — clique para ver o histórico`}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '7px',
                      border: '1.5px dashed oklch(0.76 0.01 165)',
                      color: C.sub,
                      fontSize: '10.5px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    {apelidoDoBot(x.bot)}
                  </div>
                ) : (
                  <QuadroBot
                    key={`falta-${x.bot}`}
                    rotulo={apelidoDoBot(x.bot)}
                    status={null}
                    onClick={() => onAbrirBot({ bot: x.bot, info: null, area: x.area })}
                  />
                ),
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const temMapa = !semLogs && !rotaOcultaPeloRegime;

  return (
    <div
      className="orq-elevavel"
      onClick={() => onIrParaProcessos(e.codi_emp, regime)}
      style={{
        ...CARTAO,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      {/* Faixa da situação no topo do cartão: a cor diz o estado antes do texto. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${t.dot}, color-mix(in oklch, ${t.dot} 30%, transparent))`,
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
        <AnelPercurso percurso={percurso} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <NomeEmpresa
            variante="bloco"
            nome={nomeEmpresa(e)}
            codigo={e.codi_emp}
            style={{ fontSize: '13.5px', fontWeight: 650 }}
            // Rótulo ABAIXO do código — "inativa" (ativa=false) ou "fora de
            // vigência" (ativa, mas fora da janela), pedido 09/09.
            rotuloAlerta={rotuloForaDasRegras(e)}
          />
          <div
            style={{
              display: 'flex',
              gap: '2px 8px',
              flexWrap: 'wrap',
              fontSize: '11px',
              color: C.sub,
            }}
          >
            <span>{e.empresa?.grupo_de_empresas ?? '—'}</span>
            <span style={{ color: C.faint }}>·</span>
            <span>{regime ?? '—'}</span>
          </div>
        </div>
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}
        >
          <span
            style={{
              padding: '2px 9px',
              borderRadius: '999px',
              background: t.bg,
              color: t.fg,
              fontSize: '11px',
              fontWeight: 650,
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: t.dot }} />
            {SITUACAO_NO_CARTAO[e.situacao]}
          </span>
          <span
            title="Última atividade"
            style={{
              fontFamily: FONTE_MONO,
              fontSize: '10px',
              color: C.faint,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap',
            }}
          >
            <Icone nome="relogio" tamanho={11} />
            {semLogs ? 'Sem registros ainda' : formatarAtividade(e.ultima_atividade)}
          </span>
        </div>
      </div>

      {/* Contagem por status + o botão que alterna mapa ↔ nomes, numa linha. */}
      {temMapa && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
          {(
            [
              ['ok', percurso.concluidos.length, 'concluído'],
              ['err', percurso.erros.length, 'com erro'],
              ['warn', percurso.avisos.length, 'com aviso'],
              ['run', percurso.andamento.length, 'rodando'],
              ['idle', percurso.pendentes.length, 'pendente'],
            ] as Array<[Tone, number, string]>
          )
            .filter(([, n]) => n > 0)
            .map(([tom, n, rotulo]) => (
              <span
                key={rotulo}
                style={{
                  fontSize: '10.5px',
                  padding: '1px 7px',
                  borderRadius: '999px',
                  background: TONES[tom].bg,
                  color: TONES[tom].fg,
                  fontWeight: 600,
                }}
              >
                {`${n} ${rotulo}`}
              </span>
            ))}
          {percurso.faltam.length > 0 && (
            <span
              style={{
                fontSize: '10.5px',
                padding: '0 7px',
                borderRadius: '999px',
                border: `1px dashed ${C.faint}`,
                color: C.sub,
                fontWeight: 600,
              }}
            >
              {`${percurso.faltam.length} falta${percurso.faltam.length === 1 ? '' : 'm'} percorrer`}
            </span>
          )}
          <button
            type="button"
            onClick={ev => {
              ev.stopPropagation();
              setComNomes(v => !v);
            }}
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'none',
              padding: 0,
              fontFamily: FONTE_TEXTO,
              fontSize: '11px',
              fontWeight: 600,
              color: 'oklch(0.46 0.11 240)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {comNomes ? '▴ ocultar nomes' : '▾ ver nomes'}
          </button>
        </div>
      )}

      {corpo}

      {!coberturaCarregada && !semLogs && (
        <div style={{ fontSize: '10.5px', color: C.faint }}>Conferindo os bots esperados…</div>
      )}

      {/* Erros e avisos com o MOTIVO; cada linha abre o modal do bot. */}
      {problemas.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            padding: '6px 9px',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.55)',
            border: '1px solid rgba(255,255,255,0.9)',
          }}
        >
          {problemas.slice(0, 2).map(b => {
            const tb = TONES[TONE_DO_STATUS[b.status]];
            const motivo = motivoDoBot(b) || 'sem mensagem registrada';
            return (
              <div
                key={b.bot}
                title={`${motivo} — clique para ver o erro e o histórico`}
                onClick={ev => {
                  ev.stopPropagation();
                  onAbrirBot({ bot: b.bot, info: b });
                }}
                style={{
                  display: 'flex',
                  gap: '6px',
                  alignItems: 'baseline',
                  fontSize: '11px',
                  minWidth: 0,
                  cursor: 'pointer',
                }}
              >
                <span style={{ color: tb.fg, fontWeight: 700, flex: 'none' }}>
                  {GLIFO_DO_STATUS[b.status]}
                </span>
                <span style={{ color: tb.fg, fontWeight: 700, flex: 'none' }}>
                  {apelidoDoBot(b.bot)}
                </span>
                <span
                  style={{
                    color: C.sub,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {motivo}
                </span>
              </div>
            );
          })}
          {problemas.length > 2 && (
            <div style={{ fontSize: '10.5px', color: C.faint }}>
              {`+${problemas.length - 2} outro${problemas.length - 2 === 1 ? '' : 's'} com erro ou aviso`}
            </div>
          )}
        </div>
      )}

      {/* Pares área↔responsável (pedido 08/09): fonte primária `e.areas`
          (cadastro), fallback no mapa das execuções; 2 pares + "…" e o title
          com todos. */}
      <div
        title={tituloPares}
        style={{
          display: 'flex',
          gap: '2px 12px',
          flexWrap: 'wrap',
          fontSize: '11px',
          color: C.sub,
          borderTop: '1px solid rgba(16,60,48,0.07)',
          paddingTop: '7px',
        }}
      >
        {pares.length ? (
          <>
            {paresVisiveis.map(par => (
              <span key={par.area} style={{ ...ITEM_PILHA_PAR, maxWidth: '48%' }}>
                <span style={{ color: C.faint }}>{par.area}:</span>{' '}
                <strong style={{ color: 'oklch(0.4 0.02 165)' }}>{par.responsavel}</strong>
              </span>
            ))}
            {paresTruncados && <span>…</span>}
          </>
        ) : (
          <span>Sem responsável cadastrado</span>
        )}
      </div>
    </div>
  );
};
